import React, { useEffect, useState, useRef, useCallback, useContext, useMemo } from 'react';
import SendIcon from '@mui/icons-material/Send';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PermIdentityIcon from '@mui/icons-material/PermIdentity';
import CommentIcon from '@mui/icons-material/Comment';
import ErrorIcon from '@mui/icons-material/Error';
import GroupIcon from '@mui/icons-material/Group';
import SportsEsportsIcon from '@mui/icons-material/SportsEsports';
import ReportGmailerrorredIcon from '@mui/icons-material/ReportGmailerrorred';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LockIcon from '@mui/icons-material/Lock';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import MailIcon from '@mui/icons-material/Mail';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';
import MarkEmailUnreadIcon from '@mui/icons-material/MarkEmailUnread';
import OutboxIcon from '@mui/icons-material/Outbox';
import DoneIcon from '@mui/icons-material/Done';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import { useEmail } from '../../hooks/useEmail';
import { useGroups } from '../../hooks/useGroups';
import useBranch from '../../hooks/useBranch';
import useEntertainment from '../../hooks/useEntertainment';
import useUser from '../../hooks/useUser';
import { viewRootTextPrimarySx } from '../../util/chartTheme';
import {
  Box,
  Tabs,
  Tab,
  List,
  ListItem,
  Typography,
  Paper,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  IconButton,
  Tooltip,
  CircularProgress,
  Collapse,
  Fade,
  Avatar,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Checkbox,
  useMediaQuery,
} from '@mui/material';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import { alpha, useTheme } from '@mui/material/styles';
import dayjs from 'dayjs';
import 'dayjs/locale/de';
import BookingDetailsModal from '../Bookings/BookingDetailsModal/BookingDetailsModal';
import AuthContext from '../../context/authContext';
import { useHeader } from '../../context/headerContext';
import TemplateSelectionModal from '../emails/TemplateSelectionModal';
import TextInputFeatures from '../emails/TextInputFeatures';
import GroupAssignmentModal from '../emails/GroupAssignmentModal';
import EntertainmentAssignmentModal from '../emails/EntertainmentAssignmentModal';
import { NumberChip } from '../Bookings/components/BookingIcons';
import AISuggestionCard from '../emails/AISuggestionCard';
import ComposeEmailModal from '../emails/ComposeEmailModal';
import AddIcon from '@mui/icons-material/Add';
import { toast } from 'react-toastify';
import { formatDate, stringToColor } from '../../util/formatHelper';
import { getRenderableEmailHtml } from '../../util/emailContent';

dayjs.locale('de');

const AI_SUGGESTION_POLL_ERROR_LIMIT = 3;
const AI_SUGGESTION_PENDING_TIMEOUT_MS = 2 * 60 * 1000;
const AI_SUGGESTION_STALE_ERROR_MESSAGE =
  'Der KI-Vorschlag reagiert nicht. Bitte versuche es erneut.';
const MAX_MAIL_REPLY_ATTACHMENT_BYTES = 10 * 1024 * 1024;

const formatUserDisplayName = user => {
  if (!user) return '?';
  const name = [user.firstName, user.lastName].filter(Boolean).join(' ').trim();
  if (name && user.username) return `${name} (${user.username})`;
  return user.username || name || '?';
};

const getAttachmentId = attachment => attachment?._id || attachment?.id || null;

const getAttachmentName = attachment => attachment?.name || attachment?.fileName || 'Anhang';

const getAttachmentSize = attachment => attachment?.contentLength || attachment?.size || null;

const getAttachmentByteSize = attachment =>
  Number(attachment?.size || attachment?.contentLength || 0) || 0;

export const EmailView = () => {
  const theme = useTheme();
  const assignmentChipLabelColor =
    theme.palette.mode === 'light' ? theme.palette.text.primary : theme.palette.grey[300];
  const threadBubble = useMemo(() => {
    const isLight = theme.palette.mode === 'light';
    return {
      outboundBg: isLight ? alpha(theme.palette.success.main, 0.22) : alpha(theme.palette.success.main, 0.32),
      inboundBg: isLight ? theme.palette.grey[300] : theme.palette.grey[800],
      commentBg: isLight ? alpha(theme.palette.info.main, 0.22) : alpha(theme.palette.info.main, 0.35),
    };
  }, [theme]);

  const authCtx = useContext(AuthContext);
  const isMdUp = useMediaQuery(theme.breakpoints.up('md'));
  const { registerSearch, unregisterSearch } = useHeader();
  const [listSearchTerm, setListSearchTerm] = useState('');
  const {
    fetchEmails,
    fetchEmailThread,
    sendReply,
    getMailDrafts,
    setMailDraft,
    setMailComment,
    assignGroups,
    assignEntertainment,
    updateSpamStatus,
    getMailCounts,
    getLiveDraft,
    updateMailStatus,
    runAiMailAction,
    getMailAiSuggestion,
    downloadMailAttachment,
  } = useEmail();

  const { fetchGroups } = useGroups();
  const { getBranches } = useBranch();
  const { getEntertainments } = useEntertainment();
  const { updateUserPreferences } = useUser();
  const [tabValue, setTabValue] = useState(0);
  const [showArchived, setShowArchived] = useState(false);
  const [emails, setEmails] = useState([]);
  const [selectedEmailId, setSelectedEmailId] = useState(null);
  const [emailThread, setEmailThread] = useState([]);
  const [customerName, setCustomerName] = useState(null);
  const [sendingReply, setSendingReply] = useState(false);
  const [loadingThread, setLoadingThread] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadingEmails, setLoadingEmails] = useState(true);
  const [hasMore, setHasMore] = useState(true);
  const isLoadingEmails = useRef(false);
  const defaultTextRef = useRef('');
  const [selectedBookingId, setSelectedBookingId] = useState(null);
  const [bookingModalOpen, setBookingModalOpen] = useState(false);
  const replyFieldRef = useRef(null);
  const observer = useRef();
  const [leftPanelWidth, setLeftPanelWidth] = useState(33); // Default width in percentage
  const [isResizing, setIsResizing] = useState(false);
  const containerRef = useRef(null);
  const resizeStartX = useRef(0);
  const resizeStartWidth = useRef(0);
  const [collapsingEmails, setCollapsingEmails] = useState(new Set());
  const [initialValue, setInitialValue] = useState('');
  const [fadeOut, setFadeOut] = useState(false);
  const [templateModalOpen, setTemplateModalOpen] = useState(false);
  const emailThreadRef = useRef(null);
  const [drafts, setDrafts] = useState([]);
  const [draftSnapshots, setDraftSnapshots] = useState({});
  const [loadingDrafts, setLoadingDrafts] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [showSavedIndicator, setShowSavedIndicator] = useState(false);
  const lastSavedTextRef = useRef('');
  const pollingIntervalRef = useRef(null);
  const isManualChangeRef = useRef(true);
  const currentDraftRef = useRef(null);
  const [sendingComment, setSendingComment] = useState(false);
  const [viewingDraft, setViewingDraft] = useState(null);
  const [originalText, setOriginalText] = useState('');
  const [viewingSnapshot, setViewingSnapshot] = useState(null);
  const [aiSuggestion, setAiSuggestion] = useState(null);
  const [aiSuggestionStatus, setAiSuggestionStatus] = useState('idle');
  const [aiSuggestionError, setAiSuggestionError] = useState(null);
  const aiSuggestionCacheRef = useRef({});
  const emailToCacheKeyRef = useRef({});
  const aiSuggestionPollingRef = useRef(null);
  const aiSuggestionCacheKeyRef = useRef(null);
  const [applySuggestionConfirmOpen, setApplySuggestionConfirmOpen] = useState(false);
  const [groupAssignmentModalOpen, setGroupAssignmentModalOpen] = useState(false);
  const [selectedEmailForGroupAssignment, setSelectedEmailForGroupAssignment] = useState(null);
  const [assigningGroups, setAssigningGroups] = useState(null);
  // const [branchAssignmentModalOpen, setBranchAssignmentModalOpen] = useState(false);
  // const [selectedEmailForBranchAssignment, setSelectedEmailForBranchAssignment] = useState(null);
  // const [assigningBranch, setAssigningBranch] = useState(null);

  const [groupMap, setGroupMap] = useState({});
  const [branchMap, setBranchMap] = useState({});
  const [entertainmentMap, setEntertainmentMap] = useState({});
  const [loadingMappings, setLoadingMappings] = useState(true);
  const [markingSpam, setMarkingSpam] = useState(null);
  const [emailCounts, setEmailCounts] = useState({ all: 0, spam: 0, assigned: 0 });
  const refreshTimerRef = useRef(null);
  const [showEmailView, setShowEmailView] = useState(false);
  const liveDraftPollingRef = useRef(null);
  const lastManualChangeRef = useRef(null);
  const [markingDone, setMarkingDone] = useState(null);
  const [justSentEmail, setJustSentEmail] = useState(false);
  const [suggestionApplied, setSuggestionApplied] = useState(false);
  const [executeActionsOnSend, setExecuteActionsOnSend] = useState(true);
  const [aiPanelOpen, setAiPanelOpen] = useState(false);
  const [downloadingAttachmentId, setDownloadingAttachmentId] = useState(null);
  // Bumps only so Send-button disabled logic re-evaluates; reply text stays uncontrolled in the textarea ref.
  const [replyDraftSignal, setReplyDraftSignal] = useState(0);
  const [backgroundActionHintOpen, setBackgroundActionHintOpen] = useState(false);
  const [hideBackgroundActionHintAfterConfirm, setHideBackgroundActionHintAfterConfirm] =
    useState(false);

  // not needed right now:
  // const [userAssignmentModalOpen, setUserAssignmentModalOpen] = useState(false);
  // const [selectedEmailForAssignment, setSelectedEmailForAssignment] = useState(null);
  const [entertainmentAssignmentModalOpen, setEntertainmentAssignmentModalOpen] = useState(false);
  const [selectedEmailForEntertainmentAssignment, setSelectedEmailForEntertainmentAssignment] =
    useState(null);
  const [assigningEntertainment, setAssigningEntertainment] = useState(null);
  const selectedEmail = useMemo(
    () => emails.find(email => (email._id || email.id) === selectedEmailId),
    [emails, selectedEmailId]
  );
  const [executingActionId, setExecutingActionId] = useState(null);
  const [isReplyExpanded, setIsReplyExpanded] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const fileInputRef = useRef(null);
  const [showCompose, setShowCompose] = useState(false);
  const canComposeMail = authCtx.checkRight('mail:create');
  const canSendMail = authCtx.checkRight('mail:send');

  const handleListSearch = useCallback(term => {
    setListSearchTerm(typeof term === 'string' ? term : '');
  }, []);

  const filteredEmails = useMemo(() => {
    const q = listSearchTerm.trim().toLowerCase();
    if (!q) return emails;
    return emails.filter(email => {
      const haystack = [
        email.from,
        email.to,
        email.fromName,
        email.subject,
        email.ThreadNumber,
        email.customerFirstName,
        email.customerLastName,
        email.relatedBookingNumber,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [emails, listSearchTerm]);

  useEffect(() => {
    registerSearch(
      handleListSearch,
      'E-Mails durchsuchen (Mail, Name, Buchung, Betreff)',
      listSearchTerm
    );
    return () => unregisterSearch();
  }, [registerSearch, unregisterSearch, handleListSearch, listSearchTerm]);

  const lastEmailElementRef = useCallback(
    node => {
      if (loadingMore) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver(entries => {
        if (entries[0].isIntersecting && hasMore) {
          loadMoreEmails();
        }
      });
      if (node) observer.current.observe(node);
    },
    [loadingMore, hasMore]
  );

  const stopAiSuggestionPolling = useCallback(() => {
    if (aiSuggestionPollingRef.current) {
      clearInterval(aiSuggestionPollingRef.current);
      aiSuggestionPollingRef.current = null;
    }
  }, []);

  const startAiSuggestionPolling = useCallback(
    (mailId, cacheKey) => {
      stopAiSuggestionPolling();
      let consecutiveErrors = 0;
      aiSuggestionPollingRef.current = setInterval(async () => {
        try {
          const response = await getMailAiSuggestion(mailId);
          if (!response) return;
          const { status, suggestion, error, updatedAt, cacheKey: responseCacheKey } = response;
          const effectiveCacheKey = responseCacheKey || cacheKey || mailId;

          if (status === 'ready' && suggestion?.text) {
            const cacheEntry = {
              suggestion,
              status: 'ready',
              updatedAt: updatedAt || new Date().toISOString(),
              error: null,
            };
            aiSuggestionCacheRef.current[effectiveCacheKey] = cacheEntry;
            aiSuggestionCacheRef.current[mailId] = cacheEntry;
            emailToCacheKeyRef.current[mailId] = effectiveCacheKey;
            setAiSuggestion(suggestion);
            setAiSuggestionStatus('ready');
            setAiSuggestionError(null);
            stopAiSuggestionPolling();
          } else if (status === 'failed') {
            const cacheEntry = {
              suggestion: null,
              status: 'failed',
              updatedAt: updatedAt || new Date().toISOString(),
              error: error || null,
            };
            aiSuggestionCacheRef.current[effectiveCacheKey] = cacheEntry;
            aiSuggestionCacheRef.current[mailId] = cacheEntry;
            emailToCacheKeyRef.current[mailId] = effectiveCacheKey;
            setAiSuggestionStatus('failed');
            setAiSuggestionError(error || 'Der KI-Vorschlag konnte nicht erzeugt werden.');
            stopAiSuggestionPolling();
          } else {
            setAiSuggestionStatus('pending');
            setAiSuggestionError(null);
          }
          consecutiveErrors = 0;
        } catch (error) {
          console.error('Error polling AI suggestion:', error);
          consecutiveErrors += 1;
          if (consecutiveErrors >= AI_SUGGESTION_POLL_ERROR_LIMIT) {
            stopAiSuggestionPolling();
            const failureMessage =
              error?.message || error?.code || AI_SUGGESTION_STALE_ERROR_MESSAGE;
            setAiSuggestionStatus('failed');
            setAiSuggestionError(failureMessage);
            const effectiveCacheKey = cacheKey || mailId;
            const cacheEntry = {
              suggestion: null,
              status: 'failed',
              updatedAt: new Date().toISOString(),
              error: failureMessage,
            };
            aiSuggestionCacheRef.current[effectiveCacheKey] = cacheEntry;
            aiSuggestionCacheRef.current[mailId] = cacheEntry;
            emailToCacheKeyRef.current[mailId] = effectiveCacheKey;
            toast.error('Fehler beim Laden des KI-Vorschlags');
          }
        }
      }, 6000);
    },
    [getMailAiSuggestion, stopAiSuggestionPolling]
  );

  // Helper function to set default text
  const setDefaultReplyText = (email, customerName = customerName) => {
    const greeting = `Hallo${
      customerName && customerName.firstName
        ? ` ${customerName.firstName}`
        : email.fromName
          ? ` ${email.fromName.split(' ')[0]}`
          : ''
    },\n\n`;
    const signature = '\n\nLiebe Grüße\ndein Funzone Team';
    const defaultText = greeting + signature;
    defaultTextRef.current = defaultText;
    setInitialValue(defaultText);
    if (replyFieldRef.current) {
      replyFieldRef.current.value = defaultText;
      lastSavedTextRef.current = defaultText;
    }
  };

  useEffect(() => {
    loadEmails(true, true);
  }, [tabValue, showArchived]);

  useEffect(() => {
    setAiPanelOpen(isMdUp);
  }, [isMdUp]);

  useEffect(() => {
    authCtx.refreshCountForMenuItem('E-Mails');
  }, []);

  useEffect(() => {
    if (selectedEmailId) {
      const selectedEmail = emails.find(email => (email._id || email.id) === selectedEmailId);
      if (selectedEmail) {
        lastSavedTextRef.current = selectedEmail.text;
        currentDraftRef.current = null;
      }
    }
  }, [selectedEmailId, emails]);

  const loadEmails = async (reset = false, hardReset = false) => {
    if (isLoadingEmails.current) return;

    if (reset) {
      if (hardReset) setLoadingEmails(true);
      isLoadingEmails.current = true;
      try {
        const filters = {
          onlySpam: tabValue === 2 ? 'true' : 'false',
          includeRepliedTo: showArchived ? 'true' : 'false',
          onlyAssignedToMe: tabValue === 0 ? 'true' : 'false',
        };
        const [response, counts] = await Promise.all([
          fetchEmails(filters, 1), // Always fetch page 1 on reset
          getMailCounts(),
        ]);

        const freshMails = response.mails;

        if (hardReset) {
          setEmails(freshMails);
        } else {
          // Intelligent merge to prevent scroll jump
          setEmails(currentMails => {
            if (!currentMails || currentMails.length === 0) {
              return freshMails; // Initial load
            }

            const allMails = [...freshMails, ...currentMails];
            const uniqueMails = [];
            const seenIds = new Set();
            for (const mail of allMails) {
              const mailId = mail._id || mail.id;
              if (!seenIds.has(mailId.toString())) {
                uniqueMails.push(mail);
                seenIds.add(mailId.toString());
              }
            }
            return uniqueMails;
          });
        }

        setHasMore(response.mails.length === 20);

        // Update counts from server response
        setEmailCounts({
          assigned: parseInt(counts.own || 0),
          all: parseInt(counts.open || 0),
          spam: parseInt(counts.spam || 0),
        });
      } catch (error) {
        toast.error('Fehler beim Laden der E-Mails'); // TODO: add error message
        console.error('Error loading emails:', error);
      } finally {
        isLoadingEmails.current = false;
        if (hardReset) setLoadingEmails(false);
      }
    }
  };

  // Auto refresh emails every 30 seconds
  useEffect(() => {
    const startRefreshTimer = () => {
      if (refreshTimerRef.current) {
        clearInterval(refreshTimerRef.current);
      }
      refreshTimerRef.current = setInterval(() => {
        loadEmails(true);
      }, 30000); // 30 seconds
    };

    startRefreshTimer();

    return () => {
      if (refreshTimerRef.current) {
        clearInterval(refreshTimerRef.current);
      }
    };
  }, [tabValue, showArchived]); // Reset timer when tab or archive filter changes

  const loadMoreEmails = async () => {
    if (loadingMore || !hasMore || isLoadingEmails.current) return;

    setLoadingMore(true);
    isLoadingEmails.current = true;

    try {
      const nextPage = Math.floor(emails.length / 20) + 1;
      const filters = {
        onlySpam: tabValue === 2 ? 'true' : 'false',
        includeRepliedTo: showArchived ? 'true' : 'false',
        onlyAssignedToMe: tabValue === 0 ? 'true' : 'false',
      };

      const response = await fetchEmails(filters, nextPage);

      setEmails(prev => [...prev, ...response.mails]);
      setHasMore(response.mails.length === 20);
    } catch (error) {
      console.error('Error loading more emails:', error);
      toast.error('Fehler beim Laden der E-Mails');
    } finally {
      setLoadingMore(false);
      isLoadingEmails.current = false;
    }
  };

  const handleEmailSelect = async email => {
    const emailId = email._id || email.id;
    setSelectedEmailId(emailId);
    setFadeOut(false);
    setExecutingActionId(null);
    setLoadingThread(true);
    setLoadingDrafts(true);
    setViewingDraft(null);
    currentDraftRef.current = null;
    setDraftSnapshots({});
    lastManualChangeRef.current = null;
    setSuggestionApplied(false);
    setExecuteActionsOnSend(true);

    const previousCacheKey = emailToCacheKeyRef.current[emailId];
    const cachedEntry =
      (previousCacheKey && aiSuggestionCacheRef.current[previousCacheKey]) ||
      aiSuggestionCacheRef.current[emailId] ||
      null;

    if (cachedEntry?.suggestion) {
      setAiSuggestion(cachedEntry.suggestion);
      setAiSuggestionStatus(cachedEntry.status || 'ready');
      setAiSuggestionError(cachedEntry.error || null);
    } else {
      setAiSuggestion(null);
      setAiSuggestionStatus('idle');
      setAiSuggestionError(null);
    }

    aiSuggestionCacheKeyRef.current = previousCacheKey || null;
    stopAiSuggestionPolling();

    if (window.innerWidth < 900) {
      setShowEmailView(true);
    }
    setAiPanelOpen(window.innerWidth >= 900);

    try {
      const [thread, draftsResponse] = await Promise.all([
        fetchEmailThread(emailId, { skipAiSuggestion: true }),
        getMailDrafts(emailId),
      ]);
      setEmailThread(thread.mails);
      setCustomerName(thread.customer);

      const sortedMails = [...(thread.mails || [])].sort((a, b) => {
        const getDate = email =>
          email.direction === 'inbound'
            ? new Date(email.receivedAt || email.meta?.createdAt)
            : new Date(
                email.deliveryStatus?.sentAt ||
                  email.deliveryStatus?.failedAt ||
                  email.receivedAt ||
                  email.meta?.createdAt
              );
        return getDate(a) - getDate(b);
      });

      const lastMail = sortedMails[sortedMails.length - 1];
      const isLastMailInbound = lastMail?.direction === 'inbound';

      if (isLastMailInbound) {
        const {
          aiSuggestion: threadSuggestion,
          aiSuggestionStatus: threadStatus,
          aiSuggestionError: threadError,
          aiSuggestionUpdatedAt,
          aiSuggestionCacheKey,
        } = thread;

        const cacheKey = aiSuggestionCacheKey || previousCacheKey || emailId;
        aiSuggestionCacheKeyRef.current = cacheKey;
        emailToCacheKeyRef.current[emailId] = cacheKey;

        const isStalePending =
          threadStatus === 'pending' &&
          aiSuggestionUpdatedAt &&
          dayjs().diff(dayjs(aiSuggestionUpdatedAt), 'millisecond') >
            AI_SUGGESTION_PENDING_TIMEOUT_MS;

        let activeCacheEntry =
          aiSuggestionCacheRef.current[cacheKey] ||
          (cacheKey !== emailId ? aiSuggestionCacheRef.current[emailId] : null);

        if (isStalePending) {
          const cacheEntry = {
            suggestion: null,
            status: 'failed',
            updatedAt: new Date().toISOString(),
            error: AI_SUGGESTION_STALE_ERROR_MESSAGE,
          };
          aiSuggestionCacheRef.current[cacheKey] = cacheEntry;
          aiSuggestionCacheRef.current[emailId] = cacheEntry;
          activeCacheEntry = cacheEntry;
          toast.error('Der KI-Vorschlag reagiert nicht. Bitte starte ihn neu.');
        }

        if (threadSuggestion && threadSuggestion.text) {
          const cacheEntry = {
            suggestion: threadSuggestion,
            status: 'ready',
            updatedAt: aiSuggestionUpdatedAt || new Date().toISOString(),
            error: null,
          };
          aiSuggestionCacheRef.current[cacheKey] = cacheEntry;
          aiSuggestionCacheRef.current[emailId] = cacheEntry;
          activeCacheEntry = cacheEntry;
          setAiSuggestion(threadSuggestion);
          setAiSuggestionStatus('ready');
          setAiSuggestionError(null);
          stopAiSuggestionPolling();
        } else {
          if (activeCacheEntry?.suggestion) {
            setAiSuggestion(activeCacheEntry.suggestion);
          } else {
            setAiSuggestion(null);
          }

          const effectiveStatus =
            (isStalePending ? 'failed' : threadStatus) ||
            activeCacheEntry?.status ||
            (activeCacheEntry?.suggestion ? 'ready' : 'pending');
          const effectiveError =
            (isStalePending ? AI_SUGGESTION_STALE_ERROR_MESSAGE : threadError) ||
            activeCacheEntry?.error ||
            null;

          setAiSuggestionStatus(effectiveStatus);
          setAiSuggestionError(effectiveError);

          if (
            effectiveStatus === 'pending' &&
            (!activeCacheEntry || activeCacheEntry.status !== 'ready')
          ) {
            startAiSuggestionPolling(emailId, cacheKey);
          } else if (effectiveStatus === 'failed') {
            stopAiSuggestionPolling();
          }
        }
      } else {
        setAiSuggestion(null);
        setAiSuggestionStatus('idle');
        setAiSuggestionError(null);
      }

      if (draftsResponse) {
        const draftsArray = Array.isArray(draftsResponse) ? draftsResponse : [draftsResponse];
        setDrafts(draftsArray);

        const ownDraft = draftsArray.find(draft => draft.isOwnDraft);
        if (ownDraft) {
          setInitialValue(ownDraft.text);
          if (replyFieldRef.current) {
            replyFieldRef.current.value = ownDraft.text;
            lastSavedTextRef.current = ownDraft.text;
          }
        } else {
          setDefaultReplyText(email, thread.customer);
        }
      } else {
        setDrafts([]);
        setDefaultReplyText(email, thread.customer);
      }

      if (email.lockedByOtherUser) {
        try {
          const data = await getLiveDraft(emailId);

          if (data.locked && data.draftText && replyFieldRef.current) {
            replyFieldRef.current.value = data.draftText;
            lastSavedTextRef.current = data.draftText;
            setInitialValue(data.draftText);
            setEmails(prevEmails =>
              prevEmails.map(item =>
                (item._id || item.id) === emailId
                  ? { ...item, lockedBy: data.lockedBy, lockedByUsername: data.lockedByUsername }
                  : item
              )
            );
          }
        } catch (error) {
          console.error('Error fetching live draft:', error);
          toast.error('Fehler beim Laden des Entwurfs');
        }
      }
    } catch (error) {
      toast.error('Fehler beim Laden der Entwürfe');
      console.error('Error fetching email thread or drafts:', error);
      setDrafts([]);
    } finally {
      setLoadingThread(false);
      setLoadingDrafts(false);
    }
  };

  const handleDraftSelect = draft => {
    if (replyFieldRef.current) {
      // Store the current text before showing the draft
      const currentText = replyFieldRef.current.value;
      setOriginalText(currentText);

      // Update the current draft reference and viewing state
      currentDraftRef.current = draft;
      setViewingDraft(draft);

      // Load the selected draft
      isManualChangeRef.current = false;
      replyFieldRef.current.value = draft.text;
      lastSavedTextRef.current = currentText; // Store the original text for comparison
      isManualChangeRef.current = true;
    }
  };

  const handleCancelDraft = () => {
    if (replyFieldRef.current) {
      // Restore the original text
      isManualChangeRef.current = false;
      replyFieldRef.current.value = originalText;
      isManualChangeRef.current = true;
    }
    setViewingDraft(null);
    currentDraftRef.current = null;
    setOriginalText('');
  };

  const handleAcceptDraft = async () => {
    if (replyFieldRef.current) {
      const draftText = replyFieldRef.current.value;

      // Create a snapshot with the original text
      const newSnapshot = {
        id: Date.now(),
        text: originalText,
        timestamp: new Date(),
        isOwnSnapshot: true,
      };

      setDraftSnapshots(prev => ({
        ...prev,
        [selectedEmailId]: [newSnapshot],
      }));

      // Save the draft immediately after accepting
      setSavingDraft(true);
      try {
        await setMailDraft(selectedEmailId, draftText);
        lastSavedTextRef.current = draftText;
      } catch (error) {
        console.error('Error saving draft:', error);
        toast.error('Fehler beim Speichern des Entwurfs');
      } finally {
        setSavingDraft(false);
      }
    }

    setViewingDraft(null);
    setOriginalText('');
  };

  useEffect(() => {
    const selectedEmail = emails.find(email => (email._id || email.id) === selectedEmailId);
    if (
      selectedEmail &&
      !selectedEmail.lockedByOtherUser &&
      selectedEmailId &&
      replyFieldRef.current &&
      !justSentEmail
    ) {
      lastSavedTextRef.current = replyFieldRef.current.value;
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
      pollingIntervalRef.current = setInterval(async () => {
        if (!replyFieldRef.current || viewingDraft) return;
        const currentText = replyFieldRef.current.value;
        if (
          currentText !== lastSavedTextRef.current &&
          isManualChangeRef.current &&
          currentText !== currentDraftRef.current?.text &&
          currentText !== ''
        ) {
          setSavingDraft(true);
          try {
            await setMailDraft(selectedEmailId, currentText);
            lastSavedTextRef.current = currentText;
            lastManualChangeRef.current = new Date();
            if (!viewingDraft) {
              setShowSavedIndicator(true);
              setTimeout(() => setShowSavedIndicator(false), 2000);
            }
          } catch (error) {
            console.error('Error saving draft:', error);
            toast.error('Fehler beim Speichern des Entwurfs');
          } finally {
            setSavingDraft(false);
          }
        }
      }, 5000); // Check every 5 seconds
    }
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, [selectedEmailId, viewingDraft, emails, justSentEmail]);

  const handleSnapshotSelect = snapshot => {
    if (replyFieldRef.current) {
      // Store the current text before showing the snapshot
      const currentText = replyFieldRef.current.value;

      // Only create a new snapshot if we're not already viewing one and the text has changed
      if (!viewingSnapshot && currentText !== lastSavedTextRef.current) {
        // Create a new snapshot and replace any existing ones
        const newSnapshot = {
          id: Date.now(),
          text: currentText,
          timestamp: new Date(),
          isOwnSnapshot: true,
        };

        setDraftSnapshots(prev => ({
          ...prev,
          [selectedEmailId]: [newSnapshot], // Replace array with single snapshot
        }));
      }

      setOriginalText(currentText);

      // Update the viewing state
      setViewingSnapshot(snapshot);

      // Load the selected snapshot
      isManualChangeRef.current = false;
      replyFieldRef.current.value = snapshot.text;
      lastSavedTextRef.current = currentText; // Store the original text for comparison
      isManualChangeRef.current = true;
    }
  };

  const handleCancelSnapshot = () => {
    if (replyFieldRef.current) {
      // Restore the original text
      isManualChangeRef.current = false;
      replyFieldRef.current.value = originalText;
      isManualChangeRef.current = true;
    }
    setViewingSnapshot(null);
    setOriginalText('');
  };

  const handleAcceptSnapshot = async () => {
    if (replyFieldRef.current) {
      const snapshotText = replyFieldRef.current.value;

      // Save the draft immediately after accepting
      setSavingDraft(true);
      try {
        await setMailDraft(selectedEmailId, snapshotText);
        lastSavedTextRef.current = snapshotText;
      } catch (error) {
        console.error('Error saving draft:', error);
        toast.error('Fehler beim Speichern des Entwurfs');
      } finally {
        setSavingDraft(false);
      }
    }

    setViewingSnapshot(null);
    setOriginalText('');
    // Clear the snapshot after accepting
    setDraftSnapshots(prev => ({
      ...prev,
      [selectedEmailId]: [],
    }));
  };

  const applyReplyText = text => {
    if (!replyFieldRef.current || typeof text !== 'string') return;
    isManualChangeRef.current = false;
    replyFieldRef.current.value = text;
    lastSavedTextRef.current = text;
    setInitialValue(text);
    defaultTextRef.current = text;
    isManualChangeRef.current = true;
    setReplyDraftSignal(n => n + 1);
  };

  const hasUserEditedReply = () => {
    if (!replyFieldRef.current) return false;
    const normalize = value => (value || '').replace(/\s+/g, ' ').trim();
    const current = normalize(replyFieldRef.current.value);
    const defaultText = normalize(defaultTextRef.current || '');
    if (!current && !defaultText) return false;
    return current !== defaultText;
  };

  const handleApplyAiSuggestion = () => {
    if (aiSuggestionStatus !== 'ready' || !aiSuggestion || !aiSuggestion.text) return;
    if (hasUserEditedReply()) {
      setApplySuggestionConfirmOpen(true);
      return;
    }
    applyReplyText(aiSuggestion.text);
    setSuggestionApplied(true);
    setExecuteActionsOnSend(true);
    setIsReplyExpanded(true);
    scrollToReplyField();
  };

  const scrollToReplyField = () => {
    window.setTimeout(() => {
      if (!replyFieldRef.current) return;
      replyFieldRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      replyFieldRef.current.focus();
    }, 50);
  };

  const handleOwnReplyText = () => {
    setIsReplyExpanded(true);
    scrollToReplyField();
  };

  const handleConfirmApplySuggestion = () => {
    setApplySuggestionConfirmOpen(false);
    if (aiSuggestionStatus !== 'ready' || !aiSuggestion || !aiSuggestion.text) return;
    applyReplyText(aiSuggestion.text);
    setSuggestionApplied(true);
    setExecuteActionsOnSend(true);
    setIsReplyExpanded(true);
    scrollToReplyField();
  };

  const handleCancelApplySuggestion = () => {
    setApplySuggestionConfirmOpen(false);
  };

  const handleRetryAiSuggestion = useCallback(() => {
    if (!selectedEmailId) return;
    const cacheKey =
      aiSuggestionCacheKeyRef.current ||
      emailToCacheKeyRef.current[selectedEmailId] ||
      selectedEmailId;

    if (cacheKey) {
      delete aiSuggestionCacheRef.current[cacheKey];
    }
    delete aiSuggestionCacheRef.current[selectedEmailId];

    setAiSuggestion(null);
    setAiSuggestionStatus('pending');
    setAiSuggestionError(null);

    stopAiSuggestionPolling();

    getMailAiSuggestion(selectedEmailId, { force: true })
      .then(() => {
        startAiSuggestionPolling(selectedEmailId, cacheKey);
      })
      .catch(error => {
        console.error('Error restarting AI suggestion:', error);
        toast.error('Fehler beim Starten des KI-Vorschlags');
        setAiSuggestionStatus('failed');
        setAiSuggestionError(error?.message || 'Der KI-Vorschlag konnte nicht erzeugt werden.');
      });
  }, [getMailAiSuggestion, selectedEmailId, startAiSuggestionPolling, stopAiSuggestionPolling]);

  const buildActionPreview = value => {
    if (value === null || value === undefined) return null;
    if (typeof value === 'string') return value;
    try {
      return JSON.stringify(value, null, 2);
    } catch (error) {
      return String(value);
    }
  };

  const truncatePreview = (value, limit = 2000) => {
    if (!value) return value;
    return value.length > limit ? `${value.slice(0, limit)}…` : value;
  };

  const handleRunAiAction = async action => {
    if (!selectedEmailId || !action || !action.tool) return false;
    setExecutingActionId(action.id);

    // Helper to find candidate token from previous actions
    let candidateToken = null;
    if (aiSuggestion && Array.isArray(aiSuggestion.actions)) {
      for (let i = aiSuggestion.actions.length - 1; i >= 0; i--) {
        const act = aiSuggestion.actions[i];
        const token =
          act?.result?.candidateToken ||
          act?.result?.candidate?.token ||
          act?.result?.payload?.candidateToken;

        if (token && typeof token === 'string' && token.trim()) {
          candidateToken = token.trim();
          break;
        }
      }
    }

    const baseArgs = action.arguments || action.args || {};
    const args = { ...baseArgs };
    if (candidateToken && !args.candidateToken) {
      args.candidateToken = candidateToken;
    }

    try {
      const response = await runAiMailAction(selectedEmailId, {
        toolName: action.tool,
        args,
      });
      const result =
        response && Object.prototype.hasOwnProperty.call(response, 'result')
          ? response.result
          : response;
      const preview = truncatePreview(buildActionPreview(result));
      setAiSuggestion(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          actions: prev.actions.map(item =>
            item.id === action.id
              ? {
                  ...item,
                  status: 'executed',
                  result,
                  resultPreview: preview,
                  executedAt: new Date().toISOString(),
                  error: null,
                }
              : item
          ),
        };
      });
      toast.success('Aktion erfolgreich ausgeführt');
      return true;
    } catch (error) {
      const message =
        error?.message ||
        error?.code ||
        (typeof error === 'string' ? error : 'Aktion konnte nicht ausgeführt werden');
      toast.error(message);
      setAiSuggestion(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          actions: prev.actions.map(item =>
            item.id === action.id
              ? {
                  ...item,
                  status: 'failed',
                  error: message,
                }
              : item
          ),
        };
      });
      return false;
    } finally {
      setExecutingActionId(null);
    }
  };

  // Add effect to reset snapshot state when email changes
  useEffect(() => {
    setViewingSnapshot(null);
    setOriginalText('');
    // Clear snapshots when email changes
    setDraftSnapshots(prev => ({
      ...prev,
      [selectedEmailId]: [],
    }));
  }, [selectedEmailId]);

  const handleFileSelect = async e => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    const maxSize = MAX_MAIL_REPLY_ATTACHMENT_BYTES;
    const newAttachments = [];
    let pendingTotalSize = attachments.reduce(
      (total, attachment) => total + getAttachmentByteSize(attachment),
      0
    );

    for (const file of files) {
      if (file.size > maxSize) {
        toast.error(`Die Datei "${file.name}" ist zu groß (max. 10MB)`);
        continue;
      }

      if (pendingTotalSize + file.size > maxSize) {
        toast.error(`Die Anhänge sind insgesamt zu groß (max. ${formatFileSize(maxSize)})`);
        continue;
      }

      try {
        const base64 = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => {
            const base64String = reader.result.split(',')[1];
            resolve(base64String);
          };
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });

        newAttachments.push({
          name: file.name,
          contentType: file.type || 'application/octet-stream',
          content: base64,
          size: file.size,
        });
        pendingTotalSize += file.size;
      } catch (error) {
        console.error('Error reading file:', error);
        toast.error(`Fehler beim Lesen der Datei "${file.name}"`);
      }
    }

    setAttachments(prev => [...prev, ...newAttachments]);

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRemoveAttachment = index => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const formatFileSize = bytes => {
    if (!bytes) return '';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const handleDownloadAttachment = async (mailId, attachment) => {
    const attachmentId = getAttachmentId(attachment);
    if (!mailId || !attachmentId) {
      toast.error('Anhang kann nicht heruntergeladen werden');
      return;
    }

    setDownloadingAttachmentId(attachmentId);
    try {
      const blob = await downloadMailAttachment(mailId, attachmentId);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = getAttachmentName(attachment);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading attachment:', error);
      toast.error('Fehler beim Herunterladen des Anhangs');
    } finally {
      setDownloadingAttachmentId(null);
    }
  };

  const getPendingAiActionsForSend = sendKiBodyWithoutApplying => {
    const shouldRunAiMailActions =
      (suggestionApplied || sendKiBodyWithoutApplying) && Array.isArray(aiSuggestion?.actions);

    return shouldRunAiMailActions
      ? aiSuggestion.actions.filter(
          action =>
            action.status !== 'executed' &&
            action.status !== 'blocked' &&
            action.disabled !== true
        )
      : [];
  };

  const getAiActionLabels = actions =>
    (Array.isArray(actions) ? actions : [])
      .map(action => action?.label || action?.tool)
      .filter(Boolean);

  const handleSendReply = async ({ skipBackgroundActionHint = false } = {}) => {
    const selectedEmail = emails.find(email => (email._id || email.id) === selectedEmailId);
    if (!selectedEmail || !replyFieldRef.current) return;

    const aiSuggestionTextReady =
      aiSuggestionStatus === 'ready' &&
      typeof aiSuggestion?.text === 'string' &&
      aiSuggestion.text.trim().length > 0;
    const userEditedAwayFromBaseline = hasUserEditedReply();
    const sendKiBodyWithoutApplying = !userEditedAwayFromBaseline && aiSuggestionTextReady;

    let replyContent = replyFieldRef.current.value;
    if (sendKiBodyWithoutApplying) {
      replyContent = aiSuggestion.text;
    }

    const trimmedOutgoing = typeof replyContent === 'string' ? replyContent.trim() : '';
    if (!trimmedOutgoing && attachments.length === 0) {
      return;
    }

    const pendingActions = getPendingAiActionsForSend(sendKiBodyWithoutApplying);
    const shouldShowBackgroundActionHint =
      authCtx?.preferences?.hideMailSendBackgroundActionHint !== true;

    if (
      executeActionsOnSend &&
      pendingActions.length > 0 &&
      shouldShowBackgroundActionHint &&
      !skipBackgroundActionHint
    ) {
      setHideBackgroundActionHintAfterConfirm(false);
      setBackgroundActionHintOpen(true);
      return;
    }

    setSendingReply(true);

    try {
      // Check for pending AI actions
      if (executeActionsOnSend && pendingActions.length > 0) {
        let allSuccess = true;
        for (const action of pendingActions) {
          const success = await handleRunAiAction(action);
          if (!success) {
            allSuccess = false;
          }
        }

        if (!allSuccess) {
          toast.error(
            'Nicht alle Aktionen konnten ausgeführt werden. Die E-Mail wurde nicht gesendet.'
          );
          return;
        }
      }

      const response = await sendReply(selectedEmail._id, replyContent, attachments);

      if (response.error) {
        throw new Error(response.error);
      }

      // Set justSentEmail to true to prevent draft saving
      setJustSentEmail(true);

      setAiSuggestion(null);
      setAiSuggestionStatus('idle');
      setAiSuggestionError(null);
      setSuggestionApplied(false);
      setExecuteActionsOnSend(true);

      if (selectedEmailId) {
        delete aiSuggestionCacheRef.current[selectedEmailId];
        const cacheKey = emailToCacheKeyRef.current[selectedEmailId];
        if (cacheKey) {
          delete aiSuggestionCacheRef.current[cacheKey];
        }
      }

      // Create a new email object for the sent reply
      const newEmail = {
        id: response._id || response.id,
        _id: response._id || response.id,
        direction: 'outbound',
        subject: response.subject || `Re: ${selectedEmail.subject}`,
        textBody: replyContent,
        htmlBody: replyContent.replace(/\n/g, '<br>'),
        to: response.to,
        from: response.from,
        attachments: response.attachments || attachments,
        deliveryStatus: {
          sentAt: new Date().toISOString(),
        },
      };

      // Add the new email to the thread
      setEmailThread(prevThread => [...prevThread, newEmail]);
      setInitialValue(defaultTextRef.current);

      // Reset the text field and attachments
      if (replyFieldRef.current) {
        replyFieldRef.current.value = defaultTextRef.current;
      }
      setAttachments([]);

      // Scroll to bottom after a short delay to ensure the new email is rendered
      setTimeout(() => {
        if (emailThreadRef.current) {
          emailThreadRef.current.scrollTop = emailThreadRef.current.scrollHeight;
        }
      }, 100);

      // Start fade out animation only if we're not showing archived emails
      if (!showArchived) {
        setFadeOut(true);
        setCollapsingEmails(prev => new Set([...prev, selectedEmailId]));
        // Wait for animations to complete before removing
        setTimeout(() => {
          setEmails(prevEmails =>
            prevEmails.filter(email => (email._id || email.id) !== selectedEmailId)
          );
          setCollapsingEmails(prev => {
            const newSet = new Set(prev);
            newSet.delete(selectedEmailId);
            return newSet;
          });
          setSelectedEmailId(null);
          setEmailThread([]);
          setAiSuggestion(null);
          setFadeOut(false);
          // Update email counts after removing the email
          loadEmails(true);
          authCtx.refreshCountForMenuItem('E-Mails');
          setSuggestionApplied(false);
        }, 1000);
      }
    } catch (error) {
      console.error('Error sending reply:', error);
      const attachmentsTooLarge =
        error?.statusCode === 413 ||
        error?.code === 'MAIL_REPLY_ATTACHMENTS_TOO_LARGE' ||
        error?.code === 'REQUEST_BODY_TOO_LARGE';
      toast.error(
        attachmentsTooLarge
          ? `Die Anhänge sind insgesamt zu groß (max. ${formatFileSize(
              MAX_MAIL_REPLY_ATTACHMENT_BYTES
            )})`
          : 'Fehler beim Senden der E-Mail'
      );
    } finally {
      setSendingReply(false);
    }
  };

  const handleCloseBackgroundActionHint = () => {
    if (sendingReply) return;
    setBackgroundActionHintOpen(false);
    setHideBackgroundActionHintAfterConfirm(false);
  };

  const handleConfirmBackgroundActionHint = async () => {
    setBackgroundActionHintOpen(false);

    if (hideBackgroundActionHintAfterConfirm) {
      const previousPrefs = authCtx?.preferences || {};
      const updatedPrefs = { ...previousPrefs, hideMailSendBackgroundActionHint: true };
      authCtx.setPreferences(updatedPrefs);
      try {
        const updated = await updateUserPreferences(updatedPrefs);
        if (updated && updated.preferences) {
          authCtx.setPreferences(updated.preferences);
        }
      } catch (error) {
        authCtx.setPreferences(previousPrefs);
        toast.error('Einstellung konnte nicht gespeichert werden');
      }
    }

    handleSendReply({ skipBackgroundActionHint: true });
  };

  const handleBookingClick = (e, bookingId) => {
    e.stopPropagation();
    if (bookingId) {
      setSelectedBookingId(bookingId);
      setBookingModalOpen(true);
    }
  };

  // TODO: These features are working but not needed right now:
  // const handleAssignToMe = async (e, emailId) => {
  //   e.stopPropagation();
  //   setAssigningMail(emailId);
  //   try {
  //     const updatedMail = await assignMail(emailId, true);
  //     setEmails(prevEmails =>
  //       prevEmails.map(email =>
  //         (email._id || email.id) === emailId ? updatedMail : email
  //       )
  //     );
  //   } catch (error) {
  //     console.error('Error assigning mail:', error);
  //     toast.error('Fehler beim Zuweisen der E-Mail');
  //   } finally {
  //     setAssigningMail(null);
  //   }
  // };

  // const handleAssignToUser = async (e, emailId) => {
  //   e.stopPropagation();
  //   const email = emails.find(email => (email._id || email.id) === emailId);
  //   setSelectedEmailForAssignment(email);
  //   setUserAssignmentModalOpen(true);
  // };

  // const handleUserAssignment = async (userIds) => {
  //   try {
  //     const updatedMail = await assignMail(selectedEmailForAssignment._id, false, userIds);
  //     setEmails(prevEmails =>
  //       prevEmails.map(email =>
  //         (email._id || email.id) === selectedEmailForAssignment._id ? updatedMail : email
  //       )
  //     );
  //     setUserAssignmentModalOpen(false);
  //     loadEmails(true);
  //     loadEmailCounts();
  //   } catch (error) {
  //     console.error('Error assigning mail:', error);
  //     toast.error('Fehler beim Zuweisen der E-Mail');
  //   }
  // };

  const handleAddComment = async () => {
    if (!replyFieldRef.current || replyFieldRef.current.value === '' || !selectedEmailId) return;

    const commentText = replyFieldRef.current.value;
    if (!commentText.trim()) return;

    setSendingComment(true);
    try {
      const response = await setMailComment(selectedEmailId, commentText);
      replyFieldRef.current.value = '';

      const newComment = {
        _id: response._id,
        comment: response.comment,
        createdAt: response.createdAt,
        username: response.username,
      };

      // Find the email in the thread and add the comment
      setEmailThread(prevThread =>
        prevThread.map(email => {
          if (email._id === selectedEmailId) {
            return {
              ...email,
              comments: [...(email.comments || []), newComment],
            };
          }
          return email;
        })
      );

      // Reset text field to default text
      const selectedEmail = emails.find(email => (email._id || email.id) === selectedEmailId);
      if (selectedEmail) {
        setDefaultReplyText(selectedEmail, customerName);
        if (replyFieldRef.current) {
          replyFieldRef.current.value = defaultTextRef.current;
          lastSavedTextRef.current = defaultTextRef.current;
        }
      }

      // Scroll to bottom after a short delay to ensure the new comment is rendered
      setTimeout(() => {
        if (emailThreadRef.current) {
          emailThreadRef.current.scrollTop = emailThreadRef.current.scrollHeight;
        }
      }, 100);
    } catch (error) {
      console.error('Error adding comment:', error);
      toast.error('Fehler beim Hinzufügen des Kommentars');
    } finally {
      setSendingComment(false);
    }
  };

  // Add effect to reset draft state when email changes
  useEffect(() => {
    setViewingDraft(null);
    currentDraftRef.current = null;
    setDraftSnapshots({});
    setAttachments([]);
  }, [selectedEmailId]);

  const handleAssignToGroup = async (e, emailId) => {
    e.stopPropagation();
    const email = emails.find(email => (email._id || email.id) === emailId);
    setSelectedEmailForGroupAssignment(email);
    setGroupAssignmentModalOpen(true);
  };

  const handleGroupAssignment = async groupIds => {
    try {
      const currentFilters = {
        onlySpam: tabValue === 2,
        includeRepliedTo: showArchived,
        onlyAssignedToMe: tabValue === 0,
      };
      const { updatedMail, stillMatchesFilters } = await assignGroups(
        selectedEmailForGroupAssignment._id,
        groupIds,
        currentFilters
      );

      if (stillMatchesFilters) {
        setEmails(prevEmails =>
          prevEmails.map(email =>
            (email._id || email.id) === selectedEmailForGroupAssignment._id ? updatedMail : email
          )
        );
      } else {
        setEmails(prevEmails =>
          prevEmails.filter(
            email => (email._id || email.id) !== selectedEmailForGroupAssignment._id
          )
        );
      }

      setGroupAssignmentModalOpen(false);
      loadEmails(true);
      authCtx.refreshCountForMenuItem('E-Mails');
    } catch (error) {
      console.error('Error assigning groups:', error);
      toast.error('Fehler beim Zuweisen der Gruppen');
    }
  };

  // const handleAssignToBranch = async (e, emailId) => {
  //   e.stopPropagation();
  //   const email = emails.find(email => (email._id || email.id) === emailId);
  //   setSelectedEmailForBranchAssignment(email);
  //   setBranchAssignmentModalOpen(true);
  // };

  // const handleBranchAssignment = async (branchId) => {
  //  try {
  //    const updatedMail = await assignBranch(selectedEmailForBranchAssignment._id, branchId);
  //    setEmails(prevEmails =>
  //      prevEmails.map(email =>
  //        (email._id || email.id) === selectedEmailForBranchAssignment._id ? updatedMail : email
  //      )
  //    );
  //    setBranchAssignmentModalOpen(false);
  //    loadEmails(true);
  //  } catch (error) {
  //    console.error('Error assigning branch:', error);
  //    toast.error('Fehler beim Zuweisen der Filiale');
  //  }
  // };

  // TODO: implemented but not needed right now
  const handleAssignToEntertainment = async (e, emailId) => {
    e.stopPropagation();
    const email = emails.find(email => (email._id || email.id) === emailId);
    setSelectedEmailForEntertainmentAssignment(email);
    setEntertainmentAssignmentModalOpen(true);
  };

  const handleEntertainmentAssignment = async entertainmentId => {
    if (!selectedEmailForEntertainmentAssignment?._id) {
      console.error('No email selected for entertainment assignment');
      return;
    }

    try {
      const currentFilters = {
        onlySpam: tabValue === 2,
        includeRepliedTo: showArchived,
        onlyAssignedToMe: tabValue === 0,
      };
      setAssigningEntertainment(selectedEmailForEntertainmentAssignment._id);
      const { updatedMail, stillMatchesFilters } = await assignEntertainment(
        selectedEmailForEntertainmentAssignment._id,
        entertainmentId,
        currentFilters
      );

      if (stillMatchesFilters) {
        if (updatedMail) {
          setEmails(prevEmails =>
            prevEmails.map(email =>
              (email._id || email.id) === selectedEmailForEntertainmentAssignment._id
                ? updatedMail
                : email
            )
          );
        }
      } else {
        setEmails(prevEmails =>
          prevEmails.filter(
            email => (email._id || email.id) !== selectedEmailForEntertainmentAssignment._id
          )
        );
      }

      loadEmails(true);
      authCtx.refreshCountForMenuItem('E-Mails');
    } catch (error) {
      console.error('Error assigning entertainment:', error);
      toast.error('Fehler beim Zuweisen des Entertainments');
    } finally {
      setAssigningEntertainment(null);
      setEntertainmentAssignmentModalOpen(false);
    }
  };

  // Add effect to load all mappings
  useEffect(() => {
    const loadMappings = async () => {
      setLoadingMappings(true);
      try {
        const [groupsResult, branchesResult, entertainmentsResult] = await Promise.allSettled([
          fetchGroups(),
          getBranches(),
          getEntertainments(true),
        ]);

        // Promise.allSettled returns objects with {status, value/reason}
        // We need to check if each was fulfilled before using the value
        const groups = groupsResult.status === 'fulfilled' ? groupsResult.value : [];
        const branches = branchesResult.status === 'fulfilled' ? branchesResult.value : [];
        const entertainments =
          entertainmentsResult.status === 'fulfilled' ? entertainmentsResult.value : [];

        // Create mappings
        const groupMapping = {};
        groups.forEach(group => {
          groupMapping[group._id] = group.name;
        });
        setGroupMap(groupMapping);

        const branchMapping = {};
        branches.forEach(branch => {
          branchMapping[branch._id] = branch.locationName;
        });
        setBranchMap(branchMapping);

        const entertainmentMapping = {};
        entertainments.forEach(entertainment => {
          entertainmentMapping[entertainment._id] = entertainment.entertainmentName;
        });
        setEntertainmentMap(entertainmentMapping);
      } catch (error) {
        console.error('Error loading mappings:', error);
        toast.error('Ein Fehler ist aufgetreten');
      } finally {
        setLoadingMappings(false);
      }
    };

    loadMappings();
  }, []);

  const handleUpdateSpamStatus = async (e, emailId, isSpam) => {
    e.stopPropagation();
    setMarkingSpam(emailId);
    try {
      const updatedMail = await updateSpamStatus(emailId, isSpam);

      // Remove the email from the current list in both cases
      setEmails(prevEmails => prevEmails.filter(email => (email._id || email.id) !== emailId));

      // If this was the selected email, clear the selection
      if (selectedEmailId === emailId) {
        setSelectedEmailId(null);
        setEmailThread([]);
        setAiSuggestion(null);
      }

      // Update email counts
      loadEmails(true);
    } catch (error) {
      console.error('Error updating spam status:', error);
      toast.error('Fehler beim Aktualisieren des Spam-Status');
    } finally {
      setMarkingSpam(null);
    }
  };

  const handleMarkAsDone = async (e, emailId) => {
    e.stopPropagation();
    setMarkingDone(emailId);
    try {
      const updatedMail = await updateMailStatus(emailId, 'done');

      // Remove the email from the current list
      setEmails(prevEmails => prevEmails.filter(email => (email._id || email.id) !== emailId));

      // If this was the selected email, clear the selection
      if (selectedEmailId === emailId) {
        setSelectedEmailId(null);
        setEmailThread([]);
        setAiSuggestion(null);
      }

      // Update email counts
      loadEmails(true);
    } catch (error) {
      console.error('Error marking mail as done:', error);
      toast.error('Fehler beim Markieren der E-Mail als erledigt');
    } finally {
      setMarkingDone(null);
    }
  };

  const handleMarkAsNew = async (e, emailId) => {
    e.stopPropagation();
    setMarkingDone(emailId);
    try {
      const updatedMail = await updateMailStatus(emailId, 'new');

      // Remove the email from the current list
      setEmails(prevEmails => prevEmails.filter(email => (email._id || email.id) !== emailId));

      // If this was the selected email, clear the selection
      if (selectedEmailId === emailId) {
        setSelectedEmailId(null);
        setEmailThread([]);
        setAiSuggestion(null);
      }

      // Update email counts
      loadEmails(true);
    } catch (error) {
      console.error('Error marking mail as new:', error);
      toast.error('Fehler beim Markieren der E-Mail als neu');
    } finally {
      setMarkingDone(null);
    }
  };

  const handleResizeStart = e => {
    setIsResizing(true);
    resizeStartX.current = e.clientX;
    resizeStartWidth.current = leftPanelWidth;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  };

  const handleResizeMove = useCallback(
    e => {
      if (!isResizing || !containerRef.current) return;

      const containerWidth = containerRef.current.offsetWidth;
      const deltaX = e.clientX - resizeStartX.current;
      const deltaPercentage = (deltaX / containerWidth) * 100;
      // Minimum width increased to 280px (approximately 25% on most screens)
      const minWidthPercentage = Math.max(25, (280 / containerWidth) * 100);
      const newWidth = Math.max(
        minWidthPercentage,
        Math.min(80, resizeStartWidth.current + deltaPercentage)
      );

      setLeftPanelWidth(newWidth);
    },
    [isResizing]
  );

  const handleResizeEnd = useCallback(() => {
    setIsResizing(false);
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
  }, []);

  useEffect(() => {
    if (isResizing) {
      window.addEventListener('mousemove', handleResizeMove);
      window.addEventListener('mouseup', handleResizeEnd);
    }
    return () => {
      window.removeEventListener('mousemove', handleResizeMove);
      window.removeEventListener('mouseup', handleResizeEnd);
    };
  }, [isResizing, handleResizeMove, handleResizeEnd]);

  // Add new effect to watch for locked status changes
  useEffect(() => {
    const selectedEmail = emails.find(email => (email._id || email.id) === selectedEmailId);
    if (!selectedEmail) return;

    // If email becomes locked by another user, start polling
    if (selectedEmail.lockedByOtherUser) {
      const pollLiveDraft = async () => {
        try {
          const data = await getLiveDraft(selectedEmailId);
          if (data?.locked && data?.draftText && replyFieldRef.current) {
            replyFieldRef.current.value = data.draftText;
            lastSavedTextRef.current = data.draftText;
            setInitialValue(data.draftText);
            // Only update if the lockedBy has changed
            if (selectedEmail.lockedBy !== data.lockedBy) {
              setEmails(prevEmails =>
                prevEmails.map(email =>
                  (email._id || email.id) === selectedEmailId
                    ? { ...email, lockedBy: data.lockedBy, lockedByUsername: data.lockedByUsername }
                    : email
                )
              );
            }
          }
        } catch (error) {
          console.error('Error polling live draft:', error);
          toast.error('Fehler beim Laden des Entwurfs');
        }
      };

      // Start polling immediately and then every 10 seconds
      pollLiveDraft();
      liveDraftPollingRef.current = setInterval(pollLiveDraft, 10000);
    }

    return () => {
      if (liveDraftPollingRef.current) {
        clearInterval(liveDraftPollingRef.current);
        liveDraftPollingRef.current = null;
      }
    };
  }, [selectedEmailId, emails]); // We need emails dependency here to watch for status changes

  useEffect(() => {
    setJustSentEmail(false);
  }, [selectedEmailId]);

  useEffect(() => {
    setIsReplyExpanded(false);
  }, [selectedEmailId]);

  useEffect(() => {
    return () => {
      stopAiSuggestionPolling();
    };
  }, [stopAiSuggestionPolling]);

  // Auto-scroll to bottom when email thread is loaded
  useEffect(() => {
    if (emailThread.length > 0 && !loadingThread && emailThreadRef.current) {
      // Use setTimeout to ensure the DOM is fully rendered
      setTimeout(() => {
        if (emailThreadRef.current) {
          emailThreadRef.current.scrollTop = emailThreadRef.current.scrollHeight;
        }
      }, 100);
    }
  }, [emailThread, loadingThread]);

  // Override parent container styles so the email view fills the entire remaining viewport.
  // Reverted on unmount to avoid affecting other views.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const parent = el.parentElement;
    if (!parent) return;

    const prev = {
      overflow: parent.style.overflow,
      paddingBottom: parent.style.paddingBottom,
    };
    parent.style.overflow = 'hidden';
    parent.style.paddingBottom = '0';

    return () => {
      parent.style.overflow = prev.overflow;
      parent.style.paddingBottom = prev.paddingBottom;
    };
  }, []);

  void replyDraftSignal;
  const replyDiffersFromDefault = hasUserEditedReply();
  const hasReadyKiSuggestion =
    aiSuggestionStatus === 'ready' &&
    typeof aiSuggestion?.text === 'string' &&
    aiSuggestion.text.trim().length > 0;
  const replySendBlockedEmptyBaseline =
    !replyDiffersFromDefault &&
    !hasReadyKiSuggestion &&
    attachments.length === 0;
  const sendKiBodyWithoutApplyingNow = !replyDiffersFromDefault && hasReadyKiSuggestion;
  const pendingActionsForSend = getPendingAiActionsForSend(sendKiBodyWithoutApplyingNow);
  const pendingActionLabelsForSend = getAiActionLabels(pendingActionsForSend);
  const backgroundActionDescription = pendingActionLabelsForSend.length
    ? `${pendingActionLabelsForSend.length === 1 ? 'die Aktion' : 'die Aktionen'} ${pendingActionLabelsForSend
        .map(label => `„${label}“`)
        .join(', ')}`
    : pendingActionsForSend.length === 1
      ? 'eine Hintergrundaktion'
      : 'mehrere Hintergrundaktionen';
  const detailedActionSendLabel = pendingActionLabelsForSend.length
    ? `${pendingActionLabelsForSend.length === 1 ? 'Aktion' : 'Aktionen'} ${pendingActionLabelsForSend
        .map(label => `„${label}“`)
        .join(', ')} per Bestätigungsmail auch ausführen`
    : 'Hintergrundaktionen per Bestätigungsmail auch ausführen';
  const shortActionSendLabel = pendingActionLabelsForSend.length
    ? `Auch ausführen: ${pendingActionLabelsForSend.slice(0, 2).join(', ')}${
        pendingActionLabelsForSend.length > 2 ? ` (+${pendingActionLabelsForSend.length - 2})` : ''
      }`
    : 'Aktionen auch ausführen';

  return (
    <Box
      sx={{
        ...viewRootTextPrimarySx,
        display: 'flex',
        height: 'calc(100vh - 64px)',
        overflow: 'hidden',
        position: 'relative',
        mx: -3,
      }}
      ref={containerRef}
    >
      {/* Left side - Email list */}
      <Box
        sx={{
          width: { xs: showEmailView ? 0 : '100%', md: `${leftPanelWidth}%` },
          borderRight: { xs: 0, md: 1 },
          borderColor: 'divider',
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          transition: isResizing ? 'none' : 'width 0.3s ease',
          overflow: 'hidden',
        }}
      >
        {canComposeMail && (
          <Box
            sx={{
              p: 1.5,
              borderBottom: 1,
              borderColor: 'divider',
              display: 'flex',
            }}
          >
            <Button
              variant='contained'
              fullWidth
              startIcon={<AddIcon />}
              onClick={() => setShowCompose(true)}
              sx={{ textTransform: 'none' }}
            >
              Neue E-Mail
            </Button>
          </Box>
        )}
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={(_, newValue) => setTabValue(newValue)}>
            <Tab
              sx={{ width: '33%' }}
              label={
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    fontSize: { xs: '16px', md: '12px', lg: '16px' },
                  }}
                >
                  Meine
                  {emailCounts.assigned > 0 && (
                    <NumberChip number={emailCounts.assigned} size='small' color='primary' />
                  )}
                </Box>
              }
            />
            <Tab
              sx={{ width: '33%' }}
              label={
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    fontSize: { xs: '16px', md: '12px', lg: '16px' },
                  }}
                >
                  Alle
                  {emailCounts.all > 0 && (
                    <NumberChip number={emailCounts.all} size='small' color='primary' />
                  )}
                </Box>
              }
            />
            <Tab
              sx={{ width: '33%' }}
              label={
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    fontSize: { xs: '16px', md: '12px', lg: '16px' },
                  }}
                >
                  Spam
                  {emailCounts.spam > 0 && (
                    <NumberChip number={emailCounts.spam} size='small' color='primary' />
                  )}
                </Box>
              }
            />
          </Tabs>
        </Box>

        <FormControlLabel
          control={
            <Switch checked={showArchived} onChange={e => setShowArchived(e.target.checked)} />
          }
          label='Auch erledigte anzeigen'
          sx={{ p: 2 }}
        />

        <List sx={{ flex: 1, overflow: 'auto' }}>
          {loadingEmails ? (
            <Box
              role='status'
              aria-live='polite'
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                py: 6,
                px: 2,
                minHeight: 220,
              }}
            >
              <CircularProgress size={40} color='primary' />
              <Typography variant='body2' sx={{ mt: 2, color: 'text.primary', fontWeight: 500 }}>
                E-Mails werden geladen…
              </Typography>
            </Box>
          ) : (
            filteredEmails &&
            filteredEmails.map((email, index) => (
              <Collapse
                key={email._id || email.id}
                in={!collapsingEmails.has(email._id || email.id)}
                timeout={1000}
              >
                <ListItem
                  ref={index === filteredEmails.length - 1 ? lastEmailElementRef : null}
                  onClick={() => handleEmailSelect(email)}
                  sx={{
                    cursor: 'pointer',
                    bgcolor:
                      selectedEmailId === (email._id || email.id)
                        ? 'action.selected'
                        : 'transparent',
                    '&:hover': {
                      bgcolor: 'action.hover',
                    },
                    transition: 'background-color 0.2s ease',
                  }}
                >
                  <Box sx={{ width: '100%' }}>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        pb: 1,
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: 1,
                          justifyContent: 'space-between',
                          width: '100%',
                        }}
                      >
                        <Box sx={{ wordBreak: 'break-word', maxWidth: '70%' }}>
                          <Typography
                            variant='subtitle1'
                            sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                          >
                            {email.lockedByOtherUser && (
                              <Tooltip
                                title={`Wird bearbeitet von ${email.lockedByUsername || '?'}`}
                              >
                                <LockIcon fontSize='small' color='primary' />
                              </Tooltip>
                            )}
                            {email.status === 'new' ? (
                              <MarkEmailUnreadIcon fontSize='small' color='primary' />
                            ) : (
                              <MarkEmailReadIcon fontSize='small' color='action' />
                            )}
                            {email.subject}
                          </Typography>
                          <Typography variant='caption' color='text.secondary'>
                            {formatDate(email.receivedAt)}
                          </Typography>
                          <Typography variant='body2' color='text.secondary'>
                            <Box component='span' sx={{ fontWeight: 600 }}>
                              Von:{' '}
                            </Box>
                            {email.from}
                          </Typography>
                          <Typography variant='body2' color='text.secondary'>
                            <Box component='span' sx={{ fontWeight: 600 }}>
                              An:{' '}
                            </Box>
                            {email.to}
                          </Typography>
                          {email.status === 'done' && email.doneByUsername && (
                            <Typography
                              variant='caption'
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                                color: 'success.main',
                              }}
                            >
                              <DoneAllIcon sx={{ fontSize: 14 }} />
                              Erledigt von {email.doneByUsername}
                              {email.doneAt ? ` · ${formatDate(email.doneAt)}` : ''}
                            </Typography>
                          )}
                        </Box>
                        <Box
                          sx={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(2, 1fr)',
                            justifyContent: 'flex-start',
                            alignSelf: 'flex-start',
                            height: 'fit-content',
                          }}
                        >
                          {/* TODO: This functionality is working but currently not needed */}
                          {/* <Tooltip title="Mir zuweisen">
                          <IconButton
                            size="small"
                            onClick={(e) => handleAssignToMe(e, email._id || email.id)}
                            disabled={assigningMail === (email._id || email.id)}
                            sx={{ color: 'primary.main' }}
                          >
                            {assigningMail === (email._id || email.id) ? (
                              <CircularProgress size={20} />
                            ) : (
                              <PersonAddIcon fontSize="small" />
                            )}
                          </IconButton>
                        </Tooltip>
                        {authCtx.checkRight('user:read') && (
                          <Tooltip title="Nutzer zuweisen">
                            <IconButton
                              size="small"
                              onClick={(e) => handleAssignToUser(e, email._id || email.id)}
                              sx={{ color: 'primary.main' }}
                            >
                              <AssignmentIndIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )} */}
                          {authCtx.checkRight('group:read') && (
                            <Tooltip title='Gruppen zuweisen'>
                              <IconButton
                                size='small'
                                onClick={e => handleAssignToGroup(e, email._id || email.id)}
                                sx={{ color: 'primary.main' }}
                              >
                                <GroupIcon fontSize='small' />
                              </IconButton>
                            </Tooltip>
                          )}
                          {/* TODO: not needed for now 
                        {authCtx.checkRight('branch:read:own') && (
                          <Tooltip title="Filiale zuweisen">
                            <IconButton
                              size="small"
                              onClick={(e) => handleAssignToBranch(e, email._id || email.id)}
                              sx={{ color: 'primary.main' }}
                            >
                              <ApartmentIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )} */}
                          {authCtx.checkRight('entertainment:read:own') && (
                            <Tooltip title='Entertainment zuweisen'>
                              <IconButton
                                size='small'
                                onClick={e => handleAssignToEntertainment(e, email._id || email.id)}
                                sx={{ color: 'primary.main' }}
                              >
                                <SportsEsportsIcon fontSize='small' />
                              </IconButton>
                            </Tooltip>
                          )}
                          {email.status === 'new' ? (
                            <Tooltip title='Als erledigt markieren'>
                              <IconButton
                                size='small'
                                onClick={e => handleMarkAsDone(e, email._id || email.id)}
                                disabled={markingDone === (email._id || email.id)}
                                sx={{ color: 'success.main' }}
                              >
                                {markingDone === (email._id || email.id) ? (
                                  <CircularProgress size={20} />
                                ) : (
                                  <DoneAllIcon fontSize='small' />
                                )}
                              </IconButton>
                            </Tooltip>
                          ) : (
                            (email.status === 'done' || email.status === 'replied') && (
                              <Tooltip title='Als neu markieren'>
                                <IconButton
                                  size='small'
                                  onClick={e => handleMarkAsNew(e, email._id || email.id)}
                                  disabled={markingDone === (email._id || email.id)}
                                  sx={{ color: 'primary.main' }}
                                >
                                  {markingDone === (email._id || email.id) ? (
                                    <CircularProgress size={20} />
                                  ) : (
                                    <MailIcon fontSize='small' />
                                  )}
                                </IconButton>
                              </Tooltip>
                            )
                          )}
                          {tabValue === 2 ? (
                            <Tooltip title='Als nicht Spam markieren'>
                              <IconButton
                                size='small'
                                onClick={e =>
                                  handleUpdateSpamStatus(e, email._id || email.id, false)
                                }
                                disabled={markingSpam === (email._id || email.id)}
                                sx={{ color: 'success.main' }}
                              >
                                {markingSpam === (email._id || email.id) ? (
                                  <CircularProgress size={20} />
                                ) : (
                                  <VerifiedUserIcon fontSize='small' />
                                )}
                              </IconButton>
                            </Tooltip>
                          ) : (
                            <Tooltip title='Als Spam markieren'>
                              <IconButton
                                size='small'
                                onClick={e =>
                                  handleUpdateSpamStatus(e, email._id || email.id, true)
                                }
                                disabled={markingSpam === (email._id || email.id)}
                                sx={{ color: 'error.main' }}
                              >
                                {markingSpam === (email._id || email.id) ? (
                                  <CircularProgress size={20} />
                                ) : (
                                  <ReportGmailerrorredIcon fontSize='small' />
                                )}
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                        <Box
                          sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1, width: '100%' }}
                        >
                          {email.assignedGroups?.map(groupId => {
                            const groupName = groupMap[groupId];
                            if (!groupName) return null;
                            return (
                              <Chip
                                key={groupId}
                                label={groupName}
                                size='small'
                                variant='outlined'
                                sx={{
                                  borderColor: stringToColor(groupName),
                                  bgcolor: stringToColor(groupName, 0.5),
                                  color: assignmentChipLabelColor,
                                  '& .MuiChip-label': {
                                    color: assignmentChipLabelColor,
                                  },
                                }}
                              />
                            );
                          })}
                          {email.assignedBranch && (
                            <Chip
                              label={branchMap[email.assignedBranch] || 'Unbekannte Filiale'}
                              size='small'
                              variant='outlined'
                              sx={{
                                borderColor: stringToColor(branchMap[email.assignedBranch] || ''),
                                bgcolor: stringToColor(branchMap[email.assignedBranch] || '', 0.5),
                                color: assignmentChipLabelColor,
                                '& .MuiChip-label': {
                                  color: assignmentChipLabelColor,
                                },
                              }}
                            />
                          )}
                          {email.assignedEntertainment && (
                            <Chip
                              label={
                                entertainmentMap[email.assignedEntertainment] ||
                                'Unbekanntes Entertainment'
                              }
                              size='small'
                              variant='outlined'
                              sx={{
                                borderColor: stringToColor(
                                  entertainmentMap[email.assignedEntertainment] || ''
                                ),
                                bgcolor: stringToColor(
                                  entertainmentMap[email.assignedEntertainment] || '',
                                  0.5
                                ),
                                color: assignmentChipLabelColor,
                                '& .MuiChip-label': {
                                  color: assignmentChipLabelColor,
                                },
                              }}
                            />
                          )}
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                </ListItem>
              </Collapse>
            ))
          )}
          {!loadingEmails && filteredEmails?.length === 0 && listSearchTerm.trim() && emails.length > 0 && (
            <Typography sx={{ p: 2 }} color='text.secondary' variant='body2'>
              Keine E-Mails für diese Suche.
            </Typography>
          )}
          {loadingMore && (
            <Box sx={{ ...viewRootTextPrimarySx, display: 'flex', justifyContent: 'center', p: 2 }}>
              <CircularProgress size={24} />
            </Box>
          )}
        </List>
      </Box>

      {/* Resize handler */}
      <Box
        sx={{
          display: { xs: 'none', md: 'block' },
          width: '4px',
          cursor: 'col-resize',
          bgcolor: 'transparent',
          '&:hover': {
            bgcolor: 'primary.main',
          },
          position: 'relative',
          '&::after': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: '-2px',
            width: '8px',
            height: '100%',
            cursor: 'col-resize',
          },
        }}
        onMouseDown={handleResizeStart}
      />

      {/* Right side - Email content */}
      <Box
        sx={{
          width: { xs: showEmailView ? '100%' : 0, md: `calc(100% - ${leftPanelWidth}% - 4px)` },
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          transition: isResizing ? 'none' : 'width 0.3s ease',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 2,
            bgcolor: 'background.paper',
            borderBottom: 1,
            borderColor: 'divider',
            flexShrink: 0,
          }}
        >
          {(customerName?.firstName || customerName?.lastName) && (
            <Typography variant='h6' sx={{ p: 2, pb: selectedEmailId ? 1 : 2 }}>
              Chat mit{' '}
              {customerName.firstName
                ? customerName.firstName +
                  ' ' +
                  (customerName.lastName ? customerName.lastName : '')
                : ''}
            </Typography>
          )}
          {selectedEmailId && (
            <Box
              sx={{
                display: { xs: 'flex', md: 'none' },
                alignItems: 'center',
                px: 1,
                pb: 1,
              }}
            >
              <IconButton onClick={() => setShowEmailView(false)}>
                <ArrowBackIcon />
              </IconButton>
              <Typography variant='subtitle1' sx={{ ml: 1 }}>
                {emails.find(email => (email._id || email.id) === selectedEmailId)?.subject}
              </Typography>
            </Box>
          )}
        </Box>
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflow: 'auto',
            p: 2,
          }}
          ref={emailThreadRef}
        >
          <Fade in={!fadeOut} timeout={1000}>
            <Box>
              {selectedEmailId ? (
                <>
                  {loadingThread ? (
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: '100%',
                        minHeight: '400px',
                      }}
                    >
                      <CircularProgress />
                    </Box>
                  ) : (
                    <>
                      {/* Email thread */}
                      {emailThread &&
                        [...emailThread]
                          .flatMap(email => {
                            // Create an array of all items (emails and comments) with their timestamps
                            const items = [
                              {
                                type: 'email',
                                data: email,
                                timestamp:
                                  email.direction === 'inbound'
                                    ? new Date(email.receivedAt || email.meta?.createdAt)
                                    : new Date(
                                        email.deliveryStatus?.sentAt ||
                                          email.deliveryStatus?.failedAt ||
                                          email.receivedAt ||
                                          email.meta?.createdAt
                                      ),
                                hasError:
                                  email.direction === 'outbound' && email.deliveryStatus?.failedAt,
                              },
                              ...(email.comments || []).map(comment => ({
                                type: 'comment',
                                data: comment,
                                timestamp: new Date(comment.createdAt),
                              })),
                            ];
                            return items;
                          })
                          .sort((a, b) => a.timestamp - b.timestamp)
                          .map((item, index) => (
                            <React.Fragment key={'list-item-' + index}>
                              {item.type === 'email' ? (
                                // Render email
                                <Paper
                                  sx={{
                                    p: 2,
                                    mb: 2,
                                    ml: item.data.direction === 'outbound' ? 'auto' : 0,
                                    mr: item.data.direction === 'inbound' ? 'auto' : 0,
                                    maxWidth: '80%',
                                    bgcolor:
                                      item.data.direction === 'outbound'
                                        ? threadBubble.outboundBg
                                        : threadBubble.inboundBg,
                                    borderRadius: 2,
                                    position: 'relative',
                                    wordWrap: 'break-word',
                                  }}
                                >
                                  <Box sx={{ mb: 1 }}>
                                    <Typography variant='subtitle2' sx={{ color: 'text.primary' }}>
                                      {item.data.subject}
                                    </Typography>
                                    <Typography
                                      variant='caption'
                                      sx={{ color: 'text.secondary', display: 'block' }}
                                    >
                                      {item.data.direction === 'inbound'
                                        ? 'Von: ' + item.data.from
                                        : 'An: ' + item.data.to}
                                    </Typography>
                                    <Typography
                                      variant='caption'
                                      sx={{ color: 'text.secondary', display: 'block' }}
                                    >
                                      {item.data.direction === 'inbound'
                                        ? 'An: ' + item.data.to
                                        : 'Von: ' + item.data.from}
                                    </Typography>
                                    {item.data?.direction === 'outbound' &&
                                      authCtx.checkRight('mail:read:sentByUser') &&
                                      item.data?.sentByUser && (
                                        <Typography
                                          variant='caption'
                                          sx={{ color: 'text.secondary', display: 'block' }}
                                        >
                                          {'Gesendet von: ' + formatUserDisplayName(item.data.sentByUser)}
                                        </Typography>
                                      )}
                                  </Box>
                                  <Box
                                    sx={{
                                      color: 'text.primary',
                                      '& .email-content a': {
                                        color: 'primary.main',
                                        textDecoration: 'underline',
                                      },
                                      '& .email-content p': { margin: '0.5em 0' },
                                      '& .email-content blockquote': {
                                        borderLeft: `3px solid ${theme.palette.divider}`,
                                        margin: '1em 0',
                                        paddingLeft: '1em',
                                        fontStyle: 'italic',
                                      },
                                      '& .email-content .quoted-email': {
                                        borderLeft: `2px solid ${theme.palette.divider}`,
                                        paddingLeft: '10px',
                                        margin: '10px 0',
                                        color: 'text.secondary',
                                      },
                                    }}
                                    dangerouslySetInnerHTML={{
                                      __html: `<div class="email-content">${getRenderableEmailHtml(
                                        item.data
                                      )}</div>`,
                                    }}
                                  />
                                  {Array.isArray(item.data.attachments) &&
                                    item.data.attachments.length > 0 && (
                                      <Box
                                        sx={{
                                          mt: 1.5,
                                          display: 'flex',
                                          flexWrap: 'wrap',
                                          gap: 1,
                                        }}
                                      >
                                        {item.data.attachments.map(attachment => {
                                          const attachmentId = getAttachmentId(attachment);
                                          const size = getAttachmentSize(attachment);
                                          const label = `${getAttachmentName(attachment)}${
                                            size ? ` (${formatFileSize(size)})` : ''
                                          }`;
                                          const isDownloading =
                                            attachmentId && downloadingAttachmentId === attachmentId;
                                          return (
                                            <Tooltip
                                              key={attachmentId || label}
                                              title='Anhang herunterladen'
                                            >
                                              <Chip
                                                label={label}
                                                clickable
                                                disabled={isDownloading}
                                                icon={
                                                  isDownloading ? (
                                                    <CircularProgress size={16} />
                                                  ) : (
                                                    <DownloadIcon />
                                                  )
                                                }
                                                onClick={() =>
                                                  handleDownloadAttachment(
                                                    item.data._id || item.data.id,
                                                    attachment
                                                  )
                                                }
                                                variant='outlined'
                                                size='small'
                                                sx={{
                                                  maxWidth: '320px',
                                                  bgcolor: 'background.paper',
                                                }}
                                              />
                                            </Tooltip>
                                          );
                                        })}
                                      </Box>
                                    )}
                                  <Box
                                    sx={{
                                      mt: 1,
                                      display: 'flex',
                                      justifyContent: 'space-between',
                                      alignItems: 'center',
                                    }}
                                  >
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      <Typography variant='caption' sx={{ color: 'text.secondary' }}>
                                        {dayjs(item.timestamp).format('DD.MM.YYYY HH:mm')}
                                      </Typography>
                                      {item.data?.direction === 'outbound' && (
                                        <>
                                          {item.hasError ? (
                                            <Tooltip
                                              title={`Fehler beim Senden: ${item.data.deliveryStatus?.errorMessage}`}
                                            >
                                              <ErrorIcon color='error' fontSize='small' />
                                            </Tooltip>
                                          ) : (
                                            <Tooltip
                                              title={
                                                item.data.status === 'opened'
                                                  ? 'Geöffnet'
                                                  : item.data.status === 'delivered'
                                                    ? 'Zugestellt'
                                                    : item.data.status === 'sent'
                                                      ? 'Gesendet'
                                                      : item.data.status === 'sending'
                                                        ? 'Wird gesendet'
                                                        : item.data.status === 'failed'
                                                          ? 'Fehler beim Senden'
                                                          : null
                                              }
                                            >
                                              {item.data.status === 'opened' ? (
                                                <DoneAllIcon
                                                  sx={{
                                                    color: 'primary.main',
                                                    width: 16,
                                                    height: 16,
                                                  }}
                                                />
                                              ) : item.data.status === 'delivered' ? (
                                                <DoneAllIcon
                                                  sx={{ color: 'text.secondary', width: 16, height: 16 }}
                                                />
                                              ) : item.data.status === 'sent' ? (
                                                <DoneIcon
                                                  sx={{ color: 'text.secondary', width: 16, height: 16 }}
                                                />
                                              ) : item.data.status === 'sending' ? (
                                                <OutboxIcon
                                                  sx={{ color: 'text.secondary', width: 16, height: 16 }}
                                                />
                                              ) : item.data.status === 'failed' ? (
                                                <ErrorIcon
                                                  sx={{ color: 'text.secondary', width: 16, height: 16 }}
                                                />
                                              ) : (
                                                <></>
                                              )}
                                            </Tooltip>
                                          )}
                                        </>
                                      )}
                                    </Box>
                                    {item.data.relatedInternalBookingId && (
                                      <Tooltip title='Buchungsdetails öffnen'>
                                        <Button
                                          size='small'
                                          variant='contained'
                                          color='primary'
                                          startIcon={<OpenInNewIcon />}
                                          onClick={e =>
                                            handleBookingClick(
                                              e,
                                              item.data.relatedInternalBookingId
                                            )
                                          }
                                          sx={{ ml: 2, whiteSpace: 'nowrap' }}
                                        >
                                          zur Buchung
                                        </Button>
                                      </Tooltip>
                                    )}
                                  </Box>
                                </Paper>
                              ) : (
                                // Render comment
                                <Box
                                  sx={{
                                    display: 'flex',
                                    justifyContent: 'flex-end',
                                    mb: 2,
                                  }}
                                >
                                  <Paper
                                    elevation={1}
                                    sx={{
                                      p: 2,
                                      maxWidth: '70%',
                                      backgroundColor: threadBubble.commentBg,
                                      borderRadius: 2,
                                      wordWrap: 'break-word',
                                    }}
                                  >
                                    <Typography
                                      variant='caption'
                                      color='text.secondary'
                                      sx={{ display: 'block', mb: 1 }}
                                    >
                                      Kommentar{' '}
                                      {item.data.username ? 'von ' + item.data.username : ''}
                                    </Typography>
                                    <Typography
                                      variant='body1'
                                      sx={{ whiteSpace: 'pre-wrap', color: 'text.primary' }}
                                    >
                                      {item.data.comment}
                                    </Typography>
                                    <Typography
                                      variant='caption'
                                      color='text.secondary'
                                      sx={{ display: 'block', mt: 1 }}
                                    >
                                      {dayjs(item.data.createdAt).format('DD.MM.YYYY HH:mm')}
                                    </Typography>
                                  </Paper>
                                </Box>
                              )}
                            </React.Fragment>
                          ))}
                    </>
                  )}
                </>
              ) : (
                <Typography variant='body1' sx={{ textAlign: 'center', mt: 4 }}>
                  Wähle eine E-Mail aus, um sie anzuzeigen
                </Typography>
              )}
            </Box>
          </Fade>
        </Box>

        {/* Fixed reply section */}
        {selectedEmailId && (
          <Paper
            sx={{
              p: 2,
              borderTop: 1,
              borderColor: 'divider',
              flexShrink: 0,
              bgcolor: 'background.paper',
              display: 'flex',
              flexDirection: 'column',
              maxHeight: isReplyExpanded
                ? '80vh'
                : {
                    xs: '55vh',
                    sm: '50vh',
                    md: '45vh',
                  },
              overflow: 'hidden',
            }}
          >
            <Box sx={{ display: 'flex', gap: 1, mb: 2, alignItems: 'center' }}>
              {(() => {
                const selectedEmail = emails.find(
                  email => (email._id || email.id) === selectedEmailId
                );
                const isLockedByOther = selectedEmail?.lockedByOtherUser;

                if (isLockedByOther) {
                  return (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        bgcolor: 'info.light',
                        p: 1,
                        borderRadius: 1,
                        width: '100%',
                      }}
                    >
                      <Typography variant='body2' color='info'>
                        Wird bearbeitet von {selectedEmail.lockedByUsername || '?'}
                      </Typography>
                    </Box>
                  );
                }

                if (viewingDraft) {
                  return (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        bgcolor: 'success.light',
                        p: 1,
                        borderRadius: 1,
                        width: '100%',
                      }}
                    >
                      <Typography variant='body2' color='success'>
                        Bearbeitung deaktiviert - Entwurf von {viewingDraft.user?.username || '?'}
                      </Typography>
                      <Box
                        sx={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          justifyContent: 'flex-end',
                          gap: 1,
                          ml: 'auto',
                        }}
                      >
                        <Button
                          size='small'
                          variant='outlined'
                          color='inherit'
                          onClick={handleCancelDraft}
                          sx={{
                            padding: '0px 20px',
                          }}
                        >
                          Abbrechen
                        </Button>
                        <Button
                          size='small'
                          variant='contained'
                          color='inherit'
                          onClick={handleAcceptDraft}
                          sx={{
                            padding: '0px 20px',
                          }}
                        >
                          Entwurf übernehmen
                        </Button>
                      </Box>
                    </Box>
                  );
                }

                if (viewingSnapshot) {
                  return (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        bgcolor: 'success.light',
                        p: 1,
                        borderRadius: 1,
                        width: '100%',
                      }}
                    >
                      <Typography variant='body2' color='success'>
                        Bearbeitung deaktiviert - Deine Änderungen vom{' '}
                        {dayjs(viewingSnapshot.timestamp).format('DD.MM.YYYY HH:mm')}
                      </Typography>
                      <Box
                        sx={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          justifyContent: 'flex-end',
                          gap: 1,
                          ml: 'auto',
                        }}
                      >
                        <Button
                          size='small'
                          variant='outlined'
                          color='inherit'
                          onClick={handleCancelSnapshot}
                          sx={{
                            padding: '0px 20px',
                          }}
                        >
                          Abbrechen
                        </Button>
                        <Button
                          size='small'
                          variant='contained'
                          color='inherit'
                          onClick={handleAcceptSnapshot}
                          sx={{
                            padding: '0px 20px',
                          }}
                        >
                          Änderungen übernehmen
                        </Button>
                      </Box>
                    </Box>
                  );
                }

                return null;
              })()}
            </Box>
            <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
              {(() => {
                const isLockedByOther = selectedEmail?.lockedByOtherUser;
                const hasOtherDrafts =
                  !viewingDraft && Array.isArray(drafts) && drafts.some(draft => !draft.isOwnDraft);
                const hasSnapshots = Boolean(draftSnapshots?.[selectedEmailId]?.length);

                if (!isLockedByOther && !viewingSnapshot && (hasOtherDrafts || hasSnapshots)) {
                  return (
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1 }}>
                      {!viewingDraft &&
                        drafts &&
                        drafts
                          .filter(draft => !draft.isOwnDraft)
                          .map(draft => (
                            <Tooltip
                              key={draft._id}
                              title={`Entwurf von ${
                                draft.user?.username || '?'
                              } (Zuletzt geändert: ${dayjs(draft.changedAt).format(
                                'DD.MM.YYYY HH:mm'
                              )})`}
                            >
                              <Avatar
                                sx={{
                                  width: 24,
                                  height: 24,
                                  fontSize: '0.875rem',
                                  bgcolor: 'secondary.main',
                                  cursor: 'pointer',
                                }}
                                onClick={() => handleDraftSelect(draft)}
                              >
                                {draft.user?.username ? (
                                  draft.user.username.charAt(0).toUpperCase()
                                ) : (
                                  <PermIdentityIcon fontSize='small' />
                                )}
                              </Avatar>
                            </Tooltip>
                          ))}
                      {!viewingDraft &&
                        !viewingSnapshot &&
                        draftSnapshots[selectedEmailId]?.map(snapshot => (
                          <Tooltip
                            key={snapshot.id}
                            title={`Deine Änderungen vom ${dayjs(snapshot.timestamp).format(
                              'DD.MM.YYYY HH:mm'
                            )} - Klicken zum Wiederherstellen`}
                          >
                            <Avatar
                              sx={{
                                width: 24,
                                height: 24,
                                fontSize: '0.875rem',
                                bgcolor: 'primary.main',
                                cursor: 'pointer',
                              }}
                              onClick={() => handleSnapshotSelect(snapshot)}
                            >
                              {authCtx.user?.username ? (
                                authCtx.user.username.charAt(0).toUpperCase()
                              ) : (
                                <PermIdentityIcon fontSize='small' />
                              )}
                            </Avatar>
                          </Tooltip>
                        ))}
                    </Box>
                  );
                }
                return null;
              })()}

              {aiSuggestionStatus !== 'idle' && (
                <Collapse in={aiPanelOpen} unmountOnExit>
                  <Box sx={{ mb: 2 }}>
                    <AISuggestionCard
                      suggestion={aiSuggestion}
                      status={aiSuggestionStatus}
                      errorMessage={aiSuggestionError}
                      disabled={selectedEmail?.lockedByOtherUser}
                      onApply={handleApplyAiSuggestion}
                      onOwnText={handleOwnReplyText}
                      onRunAction={handleRunAiAction}
                      executingActionId={executingActionId}
                      onRetry={handleRetryAiSuggestion}
                      forceCollapsed={isReplyExpanded}
                      reportContext={{
                        kind: 'mail',
                        mailId: selectedEmailId,
                        suggestionCacheKey: aiSuggestionCacheKeyRef.current || null,
                        context: {
                          mailId: selectedEmailId,
                          subject: selectedEmail?.subject,
                          from: selectedEmail?.from,
                          to: selectedEmail?.to,
                        },
                      }}
                    />
                  </Box>
                </Collapse>
              )}

              <Box sx={{ position: 'relative' }}>
                <TextField
                  fullWidth
                  multiline
                  minRows={isReplyExpanded ? 14 : 6}
                  maxRows={isReplyExpanded ? 28 : 12}
                  inputRef={replyFieldRef}
                  placeholder='Deine Antwort...'
                  disabled={sendingReply || viewingDraft || selectedEmail?.lockedByOtherUser}
                  defaultValue={initialValue}
                  key={selectedEmailId}
                  inputProps={{
                    onInput: () => setReplyDraftSignal(n => n + 1),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root textarea': {
                      paddingRight: '48px',
                    },
                  }}
                />
                <Tooltip title={isReplyExpanded ? 'Editor verkleinern' : 'Editor vergrößern'}>
                  <IconButton
                    size='small'
                    aria-label={
                      isReplyExpanded ? 'Antwortfeld verkleinern' : 'Antwortfeld vergrößern'
                    }
                    onClick={() => setIsReplyExpanded(prev => !prev)}
                    sx={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      zIndex: 1,
                      bgcolor: 'transparent',
                      borderRadius: '50%',
                      '&:hover': {
                        bgcolor: 'transparent',
                      },
                    }}
                  >
                    {isReplyExpanded ? (
                      <CloseFullscreenIcon fontSize='small' />
                    ) : (
                      <OpenInFullIcon fontSize='small' />
                    )}
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>

            <Box
              sx={{
                pt: 1.5,
                mt: 1,
                borderTop: 1,
                borderColor: 'divider',
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: { xs: 'stretch', sm: 'center' },
                gap: 1.5,
                flexShrink: 0,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 1.5,
                  flexWrap: 'wrap',
                }}
              >
                <TextInputFeatures
                  onTemplateSelect={() => setTemplateModalOpen(true)}
                  disabled={selectedEmail?.lockedByOtherUser}
                  textareaRef={replyFieldRef}
                />

                {(savingDraft || showSavedIndicator) && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {savingDraft && <CircularProgress size={18} />}
                    {showSavedIndicator && (
                      <Fade in={showSavedIndicator}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CheckCircleIcon color='success' sx={{ width: 18, height: 18 }} />
                          <Typography variant='caption' color='text.secondary'>
                            Entwurf gespeichert
                          </Typography>
                        </Box>
                      </Fade>
                    )}
                  </Box>
                )}
                <input
                  type='file'
                  ref={fileInputRef}
                  onChange={handleFileSelect}
                  style={{ display: 'none' }}
                  multiple
                />
                <Tooltip title='Anhang hinzufügen'>
                  <IconButton
                    onClick={() => fileInputRef.current?.click()}
                    disabled={
                      emails.find(email => (email._id || email.id) === selectedEmailId)
                        ?.lockedByOtherUser ||
                      viewingDraft ||
                      viewingSnapshot
                    }
                    color='primary'
                    size='small'
                  >
                    <AttachFileIcon />
                  </IconButton>
                </Tooltip>
              </Box>

              {attachments.length > 0 && (
                <Box sx={{ mt: 2, mb: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {attachments.map((attachment, index) => (
                    <Chip
                      key={index}
                      label={`${attachment.name} (${formatFileSize(attachment.size)})`}
                      onDelete={() => handleRemoveAttachment(index)}
                      deleteIcon={<CloseIcon />}
                      variant='outlined'
                      size='small'
                      icon={<AttachFileIcon />}
                      sx={{ maxWidth: '250px' }}
                    />
                  ))}
                </Box>
              )}

              {pendingActionsForSend.length > 0 && (
                <FormControlLabel
                  sx={{
                    m: 0,
                    alignItems: 'center',
                    '& .MuiFormControlLabel-label': { width: '100%' },
                  }}
                  control={
                    <Checkbox
                      checked={executeActionsOnSend}
                      onChange={e => setExecuteActionsOnSend(e.target.checked)}
                      color='primary'
                      size='small'
                    />
                  }
                  label={
                    <Typography
                      variant='body2'
                      color='text.secondary'
                      sx={{ wordBreak: 'break-word' }}
                    >
                      <Box component='span' sx={{ display: { xs: 'none', sm: 'inline' } }}>
                        {detailedActionSendLabel}
                      </Box>
                      <Box component='span' sx={{ display: { xs: 'inline', sm: 'none' } }}>
                        {shortActionSendLabel}
                      </Box>
                    </Typography>
                  }
                />
              )}

              <Box
                sx={{
                  display: 'flex',
                  gap: 2,
                  flexWrap: 'wrap',
                  justifyContent: 'flex-end',
                  width: { xs: '100%', sm: 'auto' },
                  ml: { xs: 0, sm: 'auto' },
                }}
              >
                <Button
                  variant='outlined'
                  color='success'
                  onClick={handleAddComment}
                  disabled={sendingComment || selectedEmail?.lockedByOtherUser}
                  startIcon={
                    sendingComment ? (
                      <CircularProgress size={20} color='inherit' />
                    ) : (
                      <CommentIcon
                        sx={{ marginLeft: { xs: '10px', sm: '0px', md: '10px', lg: '0px' } }}
                      />
                    )
                  }
                >
                  <Box sx={{ display: { xs: 'none', sm: 'inline', md: 'none', lg: 'inline' } }}>
                    {sendingComment ? 'speichern...' : 'Kommentar'}
                  </Box>
                </Button>
                <Button
                  variant='contained'
                  color='success'
                  onClick={() => handleSendReply()}
                  disabled={
                    sendingReply ||
                    selectedEmail?.lockedByOtherUser ||
                    viewingDraft ||
                    viewingSnapshot ||
                    replySendBlockedEmptyBaseline
                  }
                  startIcon={
                    sendingReply ? (
                      <CircularProgress size={20} color='inherit' />
                    ) : (
                      <SendIcon
                        sx={{ marginLeft: { xs: '10px', sm: '0px', md: '10px', lg: '0px' } }}
                      />
                    )
                  }
                >
                  <Box sx={{ display: { xs: 'none', sm: 'inline' } }}>
                    {sendingReply ? 'Wird gesendet...' : 'Senden'}
                  </Box>
                </Button>
              </Box>
            </Box>
          </Paper>
        )}
      </Box>

      {/* 
      TODO: implemented but not needed right now
      <UserAssignmentModal
        open={userAssignmentModalOpen}
        onClose={() => setUserAssignmentModalOpen(false)}
        onAssign={handleUserAssignment}
        loading={assigningMail === selectedEmailForAssignment?._id}
        assignedUsers={selectedEmailForAssignment?.assignedUsers || []}
      /> */}

      <TemplateSelectionModal
        open={templateModalOpen}
        onClose={() => setTemplateModalOpen(false)}
        onSelectTemplate={content => {
          if (replyFieldRef.current) {
            replyFieldRef.current.value = content;
            setReplyDraftSignal(n => n + 1);
          }
          setTemplateModalOpen(false);
        }}
        selectedEmail={
          selectedEmailId ? emails.find(email => (email._id || email.id) === selectedEmailId) : null
        }
      />

      <BookingDetailsModal
        open={bookingModalOpen}
        onClose={() => setBookingModalOpen(false)}
        bookingId={selectedBookingId}
        onUpdate={() => {}}
      />

      <GroupAssignmentModal
        open={groupAssignmentModalOpen}
        onClose={() => setGroupAssignmentModalOpen(false)}
        onAssign={handleGroupAssignment}
        loading={assigningGroups === selectedEmailForGroupAssignment?._id}
        assignedGroups={selectedEmailForGroupAssignment?.assignedGroups || []}
      />

      {/* 
      TODO: implemented but not needed right now
      <BranchAssignmentModal
        open={branchAssignmentModalOpen}
        onClose={() => setBranchAssignmentModalOpen(false)}
        onAssign={handleBranchAssignment}
        loading={assigningBranch === selectedEmailForBranchAssignment?._id}
        assignedBranch={selectedEmailForBranchAssignment?.assignedBranch}
      /> */}

      <EntertainmentAssignmentModal
        open={entertainmentAssignmentModalOpen}
        onClose={() => setEntertainmentAssignmentModalOpen(false)}
        onAssign={handleEntertainmentAssignment}
        loading={assigningEntertainment === selectedEmailForEntertainmentAssignment?._id}
        assignedEntertainment={selectedEmailForEntertainmentAssignment?.assignedEntertainment}
      />

      <Dialog open={backgroundActionHintOpen} onClose={handleCloseBackgroundActionHint} maxWidth='sm' fullWidth>
        <DialogTitle>Hintergrundaktion beim Mailversand</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Mit dem Senden dieser Antwort {pendingActionsForSend.length === 1 ? 'wird' : 'werden'}{' '}
            zusätzlich {backgroundActionDescription} gestartet. Der
            Kunde erhält dafür eine separate Bestätigungsmail und muss die Aktion dort bestätigen.
          </DialogContentText>
          <FormControlLabel
            control={
              <Checkbox
                checked={hideBackgroundActionHintAfterConfirm}
                onChange={e => setHideBackgroundActionHintAfterConfirm(e.target.checked)}
                color='primary'
              />
            }
            label='Diesen Hinweis nicht mehr anzeigen'
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseBackgroundActionHint} color='inherit'>
            Abbrechen
          </Button>
          <Button onClick={handleConfirmBackgroundActionHint} color='primary' variant='contained'>
            E-Mail senden
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={applySuggestionConfirmOpen} onClose={handleCancelApplySuggestion}>
        <DialogTitle>KI-Vorschlag übernehmen?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Du hast bereits einen eigenen Text geschrieben. Möchtest du ihn durch den KI-Vorschlag
            ersetzen?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelApplySuggestion} color='inherit'>
            Abbrechen
          </Button>
          <Button onClick={handleConfirmApplySuggestion} color='primary' variant='contained'>
            Übernehmen
          </Button>
        </DialogActions>
      </Dialog>

      {canComposeMail && (
        <ComposeEmailModal
          open={showCompose}
          canSend={canSendMail}
          onClose={() => setShowCompose(false)}
          onSent={() => loadEmails(true)}
        />
      )}
    </Box>
  );
};
